// ==========================================================================
// Localized country names (ENG-949)
// ==========================================================================
//
// Country names are locale *data*, not product copy, so they don't belong in
// the translation catalog — nobody should hand-translate 250 country names per
// locale. Intl.DisplayNames reads them from the browser's CLDR data, which
// covers all four of our locales including Amharic and Swahili, for which
// react-phone-number-input ships no label bundle at all.

const cache = new Map<string, Intl.DisplayNames | null>();

function displayNames(locale: string): Intl.DisplayNames | null {
  if (!cache.has(locale)) {
    try {
      cache.set(locale, new Intl.DisplayNames([locale], { type: 'region' }));
    } catch {
      // Very old engines, or a locale the runtime has no data for.
      cache.set(locale, null);
    }
  }

  return cache.get(locale) ?? null;
}

/**
 * Localized name for an ISO 3166-1 alpha-2 country code. Falls back to the
 * supplied English label (from the phone-input library) when the runtime has
 * no data, so the picker is never blank.
 */
export function countryName(code: string, locale: string, fallback: string): string {
  const names = displayNames(locale);
  if (!names) return fallback;

  try {
    // Intl.DisplayNames echoes the input back when it has no translation.
    const resolved = names.of(code.toUpperCase());

    return resolved && resolved !== code.toUpperCase() ? resolved : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Collator for sorting localized names. Amharic sorts by Ethiopic script order
 * and Spanish accents must not sort to the end, so this has to be
 * locale-aware rather than a plain string compare.
 */
export function nameCollator(locale: string): Intl.Collator {
  try {
    return new Intl.Collator(locale, { sensitivity: 'base' });
  } catch {
    return new Intl.Collator('en', { sensitivity: 'base' });
  }
}
